<template>
  <v-dialog :value="value" max-width="600px" @input="$emit('input', $event)">
    <v-card>
      <v-card-title>
        <span class="headline">
          {{ partner ? $t("partners.editPartner") : $t("partners.newPartner") }}
        </span>
      </v-card-title>
      <v-card-text>
        <v-form ref="form">
          <v-container>
            <v-text-field
              v-model="form.company"
              :rules="requiredRules"
              :label="$t('partners.headers.company')"
              outlined
            ></v-text-field>
            <v-text-field
              v-model="form.service"
              :rules="requiredRules"
              :label="$t('partners.headers.service')"
              outlined
            ></v-text-field>
            <v-text-field
              v-model="form.contact"
              :label="$t('partners.headers.contact')"
              outlined
            ></v-text-field>
            <v-text-field
              v-model="form.phone_number"
              :label="$t('partners.headers.phone_number')"
              outlined
            ></v-text-field>
            <v-text-field
              v-model="form.email"
              :rules="emailRules"
              :label="$t('partners.headers.email')"
              outlined
            ></v-text-field>
            <v-text-field
              v-model="form.website"
              :rules="websiteRules"
              :label="$t('partners.headers.website')"
              placeholder="https://"
              outlined
            ></v-text-field>
            <v-img
              v-if="imagePreview"
              :src="imagePreview"
              max-height="120"
              contain
              class="mb-4"
            ></v-img>
            <v-file-input
              ref="imageInput"
              v-model="image"
              :rules="imageRules"
              :label="$t('partners.image')"
              :hint="$t('partners.imageHint')"
              :accept="IMAGE_CONSTRAINTS.types.join(',')"
              persistent-hint
              prepend-icon="mdi-image-outline"
              outlined
              class="mb-2"
            >
              <template v-slot:append-outer>
                <v-tooltip left max-width="320">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon color="#2b58e2" v-bind="attrs" v-on="on">
                      mdi-information-outline
                    </v-icon>
                  </template>
                  <p class="font-weight-bold mb-1">
                    {{ $t("partners.imageInfo.title") }}
                  </p>
                  <ul class="pl-4">
                    <li>{{ $t("partners.imageInfo.formats") }}</li>
                    <li>{{ $t("partners.imageInfo.size") }}</li>
                    <li>{{ $t("partners.imageInfo.ratio") }}</li>
                    <li>{{ $t("partners.imageInfo.spotlight") }}</li>
                  </ul>
                </v-tooltip>
              </template>
            </v-file-input>
            <v-switch
              v-model="form.is_spotlight"
              :label="$t('partners.spotlight')"
              :hint="$t('partners.spotlightHint')"
              persistent-hint
              inset
              class="mt-0"
            ></v-switch>
          </v-container>
        </v-form>
        <v-alert v-if="error" type="error" dense text>
          {{ $t("partners.errorOccurred") }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="blue darken-1" text @click="$emit('input', false)">
          {{ $t("partners.close") }}
        </v-btn>
        <v-btn :disabled="loading" color="blue darken-1" text @click="save">
          {{ $t("partners.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import * as Sentry from "@sentry/vue";
import partners from "@/api/partners";

const IMAGE_CONSTRAINTS = {
  types: ["image/jpeg", "image/png", "image/webp"],
  maxSizeBytes: 10 * 1024 * 1024,
  minRatio: 4,
  maxRatio: 8,
};

function measureImageRatio(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img.naturalWidth / img.naturalHeight);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

const EMPTY_FORM = {
  company: "",
  service: "",
  contact: "",
  phone_number: "",
  email: "",
  website: "",
  is_spotlight: false,
};

export default {
  name: "PartnerDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    partner: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      form: { ...EMPTY_FORM },
      image: null,
      previewUrl: "",
      imageRatio: null,
      IMAGE_CONSTRAINTS,
      loading: false,
      error: false,
      requiredRules: [(v) => !!v || this.$t("partners.validation.required")],
      emailRules: [
        (v) =>
          !v || /.+@.+\..+/.test(v) || this.$t("partners.validation.email"),
      ],
      websiteRules: [
        (v) =>
          !v ||
          /^https?:\/\/.+\..+/.test(v) ||
          this.$t("partners.validation.website"),
      ],
      imageRules: [
        (file) =>
          !this.form.is_spotlight ||
          !!file ||
          !!this.partner?.image_url ||
          this.$t("partners.validation.imageRequired"),
        (file) =>
          !file ||
          IMAGE_CONSTRAINTS.types.includes(file.type) ||
          this.$t("partners.validation.imageType"),
        (file) =>
          !file ||
          file.size <= IMAGE_CONSTRAINTS.maxSizeBytes ||
          this.$t("partners.validation.imageSize"),
        (file) =>
          !file ||
          (this.imageRatio >= IMAGE_CONSTRAINTS.minRatio &&
            this.imageRatio <= IMAGE_CONSTRAINTS.maxRatio) ||
          this.$t("partners.validation.imageRatio"),
      ],
    };
  },
  computed: {
    imagePreview() {
      return this.previewUrl || this.partner?.image_url || "";
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) this.resetForm();
    },
    async image(file) {
      this.releasePreview();
      this.imageRatio = null;
      if (!file) return this.validateImage();
      this.previewUrl = URL.createObjectURL(file);
      const ratio = await measureImageRatio(this.previewUrl);
      if (this.image !== file) return;
      this.imageRatio = ratio;
      this.validateImage();
    },
    "form.is_spotlight"() {
      this.validateImage();
    },
  },
  beforeDestroy() {
    this.releasePreview();
  },
  methods: {
    releasePreview() {
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = "";
    },
    validateImage() {
      this.$refs.imageInput?.validate(true);
    },
    resetForm() {
      this.form = this.partner
        ? Object.fromEntries(
            Object.keys(EMPTY_FORM).map((key) => [
              key,
              this.partner[key] ?? EMPTY_FORM[key],
            ]),
          )
        : { ...EMPTY_FORM };
      this.image = null;
      this.error = false;
      this.$refs.form?.resetValidation();
    },
    buildFormData() {
      const formData = new FormData();
      Object.entries(this.form).forEach(([key, value]) => {
        formData.append(key, value);
      });
      if (this.image) formData.append("image", this.image);
      if (this.partner) formData.append("partner_id", this.partner.id);
      return formData;
    },
    async save() {
      if (!this.$refs.form.validate()) return;
      this.loading = true;
      this.error = false;
      try {
        const formData = this.buildFormData();
        const response = this.partner
          ? await partners.updatePartner(formData)
          : await partners.createPartner(formData);
        if (response.code == 200) {
          this.$emit("saved");
          this.$emit("input", false);
        } else {
          this.error = true;
        }
      } catch (error) {
        Sentry.captureException(error);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
